import { type FormEvent, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  fetchSample,
  useSourceSchemaCatalog,
  useTableDetail,
  type SampleResponse,
  type SourceSchemaCatalogRow,
  type TableDetailResponse,
} from '../hooks/api';
import { Loading, ErrorBox, EmptyBox } from '../components/Loading';
import { Status } from '../components/Status';
import { formatBytes, formatNumber } from '../api';

const SORTABLE_TYPES = new Set([
  'bigint',
  'boolean',
  'character',
  'character varying',
  'date',
  'double precision',
  'integer',
  'numeric',
  'real',
  'smallint',
  'text',
  'timestamp with time zone',
  'timestamp without time zone',
  'uuid',
]);

export default function TableDetailPage() {
  const { tenantCode, schema, table } = useParams<{
    tenantCode: string;
    schema: string;
    table: string;
  }>();
  const [params, setParams] = useSearchParams();
  const detail = useTableDetail(tenantCode, schema, table);
  const limit = clampInt(params.get('limit'), 50, 1, 200);
  const offset = clampInt(params.get('offset'), 0, 0, 100000);
  const orderBy = params.get('order_by') ?? '';
  const orderDir = params.get('order_dir') === 'asc' ? 'asc' : 'desc';
  const q = params.get('q') ?? '';
  const payloadParam = params.get('payload');
  const includePayload = payloadParam === null ? true : payloadParam === '1';
  const [searchInput, setSearchInput] = useState(q);
  const catalog = useSourceSchemaCatalog(tenantCode, schema, table);

  useEffect(() => setSearchInput(q), [q]);

  const sample = useQuery({
    queryKey: [
      'table-sample',
      tenantCode,
      schema,
      table,
      limit,
      offset,
      orderBy,
      orderDir,
      q,
      includePayload,
    ],
    queryFn: () => {
      const options: {
        includePayload: boolean;
        limit: number;
        offset: number;
        orderBy?: string;
        orderDir: 'asc' | 'desc';
        q?: string;
      } = {
        includePayload,
        limit,
        offset,
        orderDir,
      };
      if (orderBy) options.orderBy = orderBy;
      if (q) options.q = q;
      return fetchSample(tenantCode!, schema!, table!, options);
    },
    enabled: !!(tenantCode && schema && table),
  });

  if (!tenantCode || !schema || !table) return <ErrorBox error="Pfad-Parameter fehlen" />;

  const applySearch = (event: FormEvent): void => {
    event.preventDefault();
    updateParams(setParams, params, { q: searchInput.trim() || null, offset: '0' });
  };

  const goToOffset = (nextOffset: number): void => {
    updateParams(setParams, params, { offset: String(Math.max(nextOffset, 0)) });
  };

  const pageEnd = sample.data ? sample.data.offset + sample.data.row_count : 0;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between border-b border-neutral-300 pb-1">
        <div>
          <h1 className="text-[14px] font-mono font-semibold text-neutral-700">
            {schema}.{table}
          </h1>
          <div className="text-[11px] text-neutral-600">
            Generischer Read-only Tabellenbrowser fuer alle freigegebenen Schemas.
          </div>
        </div>
        <Link to={`/tenants/${encodeURIComponent(tenantCode)}`} className="btn">
          Zurueck
        </Link>
      </div>

      {detail.isLoading && <Loading />}
      {detail.error && <ErrorBox error={detail.error} />}

      {detail.data && (
        <>
          <div className="grid grid-cols-3 gap-3">
            <KpiPanel
              title="Zeilen (Schaetzwert)"
              value={formatNumber(detail.data.stats.row_estimate)}
            />
            <KpiPanel
              title="Gesamt-Groesse"
              value={formatBytes(detail.data.stats.total_size_bytes)}
            />
            <KpiPanel
              title="Indices-Groesse"
              value={formatBytes(detail.data.stats.indexes_size_bytes)}
            />
          </div>

          <div className="panel">
            <div className="panel-h flex items-center justify-between">
              <span>Daten</span>
              <span className="text-[10px] text-neutral-500">
                Read-only, PII redacted, 5s Statement-Timeout
              </span>
            </div>
            <div className="panel-b space-y-2">
              <form className="flex flex-wrap items-center gap-2" onSubmit={applySearch}>
                <input
                  className="input w-[320px]"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Suchen, z.B. Artikelnummer, Name, EAN"
                />
                <button type="submit" className="btn-primary">
                  Suchen
                </button>
                {q && (
                  <button
                    type="button"
                    className="btn"
                    onClick={() => {
                      setSearchInput('');
                      updateParams(setParams, params, { q: null, offset: '0' });
                    }}
                  >
                    Suche leeren
                  </button>
                )}
                <select
                  className="input"
                  value={String(limit)}
                  onChange={(e) => updateParams(setParams, params, { limit: e.target.value, offset: '0' })}
                >
                  <option value="25">25 Zeilen</option>
                  <option value="50">50 Zeilen</option>
                  <option value="100">100 Zeilen</option>
                  <option value="200">200 Zeilen</option>
                </select>
                <label className="inline-flex items-center gap-1 text-[12px] text-neutral-700">
                  <input
                    type="checkbox"
                    checked={includePayload}
                    onChange={(e) =>
                      updateParams(setParams, params, {
                        payload: e.target.checked ? '1' : '0',
                        offset: '0',
                      })
                    }
                  />
                  Payload-Felder als Spalten
                </label>
                <button
                  type="button"
                  className="btn"
                  disabled={!sample.data?.rows.length}
                  onClick={() => sample.data && exportCurrentRows(sample.data)}
                >
                  CSV aktuelle Ansicht
                </button>
              </form>

              {sample.error && <ErrorBox error={sample.error} />}
              {sample.isLoading && <Loading />}
              {sample.data && (
                <SampleTable
                  detail={detail.data}
                  sample={sample.data}
                  orderBy={orderBy}
                  orderDir={orderDir}
                  onSort={(columnName) => {
                    const nextDir = orderBy === columnName && orderDir === 'desc' ? 'asc' : 'desc';
                    updateParams(setParams, params, {
                      order_by: columnName,
                      order_dir: nextDir,
                      offset: '0',
                    });
                  }}
                />
              )}
              {!sample.isLoading && !sample.error && !sample.data && (
                <EmptyBox>Keine Daten geladen.</EmptyBox>
              )}
            </div>
            <div className="px-3 py-2 border-t border-neutral-300 flex items-center justify-between text-[12px]">
              <span className="text-neutral-600">
                {sample.data
                  ? sample.data.row_count > 0
                    ? `Zeilen ${formatNumber(sample.data.offset + 1)}-${formatNumber(pageEnd)}`
                    : 'Keine Zeilen fuer diese Ansicht'
                  : 'Keine Zeilen geladen'}
                {q ? ` | Suche: "${q}"` : ''}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="btn"
                  disabled={offset <= 0 || sample.isFetching}
                  onClick={() => goToOffset(offset - limit)}
                >
                  Zurueck
                </button>
                <button
                  type="button"
                  className="btn"
                  disabled={!sample.data?.has_more || sample.isFetching}
                  onClick={() => goToOffset(offset + limit)}
                >
                  Weiter
                </button>
              </div>
            </div>
          </div>

          <div className="panel">
            <div className="panel-h">Spalten ({detail.data.columns.length})</div>
            <div className="panel-b p-0">
              <table className="tbl">
                <thead>
                  <tr>
                    <th className="num" style={{ width: 40 }}>
                      #
                    </th>
                    <th>Name</th>
                    <th>Typ</th>
                    <th>Nullable</th>
                    <th>Default</th>
                    <th>Hinweis</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.data.columns.map((c) => (
                    <tr key={c.name}>
                      <td className="num">{c.position}</td>
                      <td className="mono">{c.name}</td>
                      <td className="mono text-[11.5px]">{c.data_type}</td>
                      <td>
                        {c.nullable ? (
                          <span className="text-neutral-500">NULL</span>
                        ) : (
                          <span className="text-st-err font-semibold">NOT NULL</span>
                        )}
                      </td>
                      <td className="mono text-[11px] text-neutral-600">{c.default ?? ''}</td>
                      <td className="space-x-1">
                        {c.is_pii ? <Status kind="warn">PII</Status> : ''}
                        {c.is_large_json ? <Status kind="muted">Payload</Status> : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {schema === 'sources' && (
            <SourceSchemaCatalogPanel
              rows={catalog.data?.data ?? []}
              loading={catalog.isLoading}
              error={catalog.error}
            />
          )}

          <div className="panel">
            <div className="panel-h">Indices ({detail.data.indexes.length})</div>
            <div className="panel-b p-0">
              {detail.data.indexes.length === 0 ? (
                <div className="p-2">
                  <EmptyBox>Keine Indices.</EmptyBox>
                </div>
              ) : (
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Definition</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.data.indexes.map((i) => (
                      <tr key={i.name}>
                        <td className="mono">{i.name}</td>
                        <td>
                          {i.primary && <Status kind="info">PK</Status>}
                          {!i.primary && i.unique && <Status kind="info">UNIQUE</Status>}
                          {!i.primary && !i.unique && (
                            <span className="text-neutral-500 text-[11px]">btree</span>
                          )}
                        </td>
                        <td className="mono text-[11px]">{i.definition}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="panel">
            <div className="panel-h">Foreign Keys ({detail.data.foreign_keys.length})</div>
            <div className="panel-b p-0">
              {detail.data.foreign_keys.length === 0 ? (
                <div className="p-2">
                  <EmptyBox>Keine Foreign Keys.</EmptyBox>
                </div>
              ) : (
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Spalte</th>
                      <th>Verweist auf</th>
                      <th>ON DELETE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.data.foreign_keys.map((f) => (
                      <tr key={f.name}>
                        <td className="mono text-[11px]">{f.name}</td>
                        <td className="mono">{f.column}</td>
                        <td className="mono">
                          {f.references.schema}.{f.references.table}({f.references.column})
                        </td>
                        <td className="mono text-[11px]">{f.on_delete}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SampleTable({
  detail,
  sample,
  orderBy,
  orderDir,
  onSort,
}: {
  detail: TableDetailResponse;
  sample: SampleResponse;
  orderBy: string;
  orderDir: 'asc' | 'desc';
  onSort: (columnName: string) => void;
}) {
  if (sample.row_count === 0) {
    return <EmptyBox>Tabelle ist leer oder die Suche liefert keine Treffer.</EmptyBox>;
  }

  const detailByName = new Map(detail.columns.map((c) => [c.name, c]));
  return (
    <div className="overflow-x-auto border border-neutral-300">
      {sample.omitted_columns.length > 0 && (
        <div className="px-2 py-1 text-[11px] text-neutral-600 bg-neutral-50 border-b border-neutral-300">
          Ausgeblendet: {sample.omitted_columns.map((c) => c.name).join(', ')}.
          Payload kann oben zugeschaltet werden.
        </div>
      )}
      <table className="tbl">
        <thead>
          <tr>
            {sample.columns.map((c) => {
              const meta = detailByName.get(c.name);
              const sortable = meta ? SORTABLE_TYPES.has(meta.data_type) : false;
              const active = orderBy === c.name || (!orderBy && sample.order_by === c.name);
              return (
                <th key={c.name}>
                  <button
                    type="button"
                    className={sortable ? 'font-mono hover:underline' : 'font-mono text-neutral-500'}
                    disabled={!sortable}
                    onClick={() => sortable && onSort(c.name)}
                  >
                    {c.name}
                    {active ? (orderDir === 'asc' ? ' ^' : ' v') : ''}
                    {c.redacted ? ' [PII]' : ''}
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sample.rows.map((row, i) => (
            <tr key={i}>
              {sample.columns.map((c) => (
                <td key={c.name} className="mono text-[11px] max-w-[340px] truncate" title={formatCell(row[c.name])}>
                  {formatCell(row[c.name])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SourceSchemaCatalogPanel({
  rows,
  loading,
  error,
}: {
  rows: SourceSchemaCatalogRow[];
  loading: boolean;
  error: unknown;
}) {
  const statusCounts = rows.reduce<Record<string, number>>((acc, row) => {
    acc[row.status] = (acc[row.status] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <div className="panel">
      <div className="panel-h flex items-center justify-between">
        <span>Source-Schema-Katalog</span>
        <span className="text-[10px] text-neutral-500">
          {rows.length} Felder
          {Object.entries(statusCounts).map(([status, count]) => ` | ${status}: ${count}`).join('')}
        </span>
      </div>
      <div className="panel-b p-0">
        {loading && (
          <div className="p-2">
            <Loading />
          </div>
        )}
        {error != null && (
          <div className="p-2">
            <ErrorBox error={error} />
          </div>
        )}
        {!loading && !error && rows.length === 0 && (
          <div className="p-2">
            <EmptyBox>Noch kein Katalog-Lauf fuer diese Source-Tabelle.</EmptyBox>
          </div>
        )}
        {rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Feldpfad</th>
                  <th>Typen</th>
                  <th>Status</th>
                  <th className="num">Letzter Scan</th>
                  <th>Beispiel</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={`${row.payload_column}.${row.field_path}`}>
                    <td className="mono">{row.field_path}</td>
                    <td className="mono text-[11px]">{row.value_types.join(', ')}</td>
                    <td>
                      <Status kind={statusKind(row.status)}>{row.status}</Status>
                    </td>
                    <td className="num">{formatNumber(row.last_scan_count)}</td>
                    <td className="mono text-[11px] max-w-[320px] truncate" title={formatCell(row.sample_value)}>
                      {formatCell(row.sample_value)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function statusKind(status: SourceSchemaCatalogRow['status']): 'ok' | 'warn' | 'err' | 'info' | 'muted' {
  switch (status) {
    case 'new':
      return 'info';
    case 'type_changed':
      return 'warn';
    case 'missing':
      return 'err';
    case 'known':
      return 'ok';
  }
}

function formatCell(v: unknown): string {
  if (v === null || v === undefined) return '-';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (v instanceof Date) return v.toISOString();
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

function KpiPanel({ title, value }: { title: string; value: string }) {
  return (
    <div className="panel">
      <div className="panel-h">{title}</div>
      <div className="panel-b">
        <div className="text-[18px] font-mono font-semibold text-slate-900">{value}</div>
      </div>
    </div>
  );
}

function updateParams(
  setParams: ReturnType<typeof useSearchParams>[1],
  current: URLSearchParams,
  updates: Record<string, string | null>,
): void {
  const next = new URLSearchParams(current);
  for (const [key, value] of Object.entries(updates)) {
    if (value === null || value === '') next.delete(key);
    else next.set(key, value);
  }
  setParams(next);
}

function clampInt(value: string | null, fallback: number, min: number, max: number): number {
  const parsed = Number.parseInt(value ?? '', 10);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(Math.max(parsed, min), max);
}

function exportCurrentRows(sample: SampleResponse): void {
  const headers = sample.columns.map((c) => c.name);
  const lines = [
    headers.map(csvCell).join(';'),
    ...sample.rows.map((row) => headers.map((h) => csvCell(row[h])).join(';')),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${sample.schema}.${sample.table}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

function csvCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  const text = typeof value === 'object' ? JSON.stringify(value) : String(value);
  return `"${text.replace(/"/g, '""')}"`;
}
